import { type JSX } from 'react';
import { useGate, useUnit } from 'effector-react';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
  type AutocompleteProps,
} from '@mui/material';
import { Listbox } from '../../components/listbox';
import { withControl } from '../../hoc/withControl';
import { getAutocompleteOnChange } from '../../lib/ get-autocomplete-on-change';
import { getAutocompleteValue } from '../../lib/get-autocomplete-value';
import { TypesResidentsImportGate, typesResidentsImportQuery } from './model';
import type { ResidentImportType } from './types';

export const TypesResidentsImportControl = withControl<
  Partial<AutocompleteProps<ResidentImportType, boolean, boolean, boolean>>
>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    isError,
    multiple = false,
  }) => {
    useGate(TypesResidentsImportGate);
    const { data, pending } = useUnit(typesResidentsImportQuery);

    const handleChange = (
      _: unknown,
      newValue: string | ResidentImportType | (string | ResidentImportType)[] | null
    ) => getAutocompleteOnChange(newValue, setValue, multiple);

    return (
      <FormControl required={required} disabled={disabled}>
        {label && (
          <FormLabel required={!readOnly && required} error={isError}>
            {label}
          </FormLabel>
        )}
        <Autocomplete
          multiple={multiple}
          disableCloseOnSelect={multiple}
          loading={pending}
          value={getAutocompleteValue(data, value, multiple)}
          options={!data ? [] : data}
          onChange={handleChange}
          slotProps={{
            listbox: {
              component: Listbox,
            },
          }}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              slotProps={{
                input: {
                  ...params.InputProps,
                  readOnly,
                  error: isError,
                  endAdornment: readOnly ? null : (
                    <>
                      {pending && <CircularProgress size={20} color="inherit" />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          readOnly={readOnly}
          disabled={disabled}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    );
  }
);
